import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { TextContainerPosition } from 'graphql-ts-types';
import {
  backgroundColor as bg,
  display,
  left,
  maxHeight,
  opacity,
  position,
  right,
  space,
  top,
  width,
} from 'styled-system';
import type {
  BackgroundColorProps,
  DisplayProps,
  LeftProps,
  MaxHeightProps,
  OpacityProps,
  PositionProps,
  RightProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import type { Color } from 'common/theme/colors/types';
import {
  boxSizing,
  overflowY,
  type BoxSizingProps,
  type OverflowYProps,
  type TransformProps,
  type Translate3DProps,
} from 'common/theme/system-utilities';
import { HALF_SIZE_VARIANT_TEXTBOX_WIDTH } from '../../../constants';
import type { BannerProps } from '../../../types';

interface FloatingTextBoxProps extends Partial<BannerProps>, PropsWithChildren {
  isTextMissing?: boolean;
}

const Container = styled.div<
  WidthProps &
    SpaceProps &
    OpacityProps &
    BackgroundColorProps &
    BoxSizingProps &
    DisplayProps &
    PositionProps &
    RightProps &
    MaxHeightProps &
    Translate3DProps &
    TransformProps &
    LeftProps & { transform?: string }
>`
  ${width};
  ${space};
  ${display};
  ${boxSizing};
  ${position};
  ${maxHeight};
  ${(props: { transform?: string }) => props.transform && `transform: ${props.transform};`}
  ${right}
  ${left}
  ${top}
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${bg};
    ${opacity};
    z-index: 0;
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  width: HALF_SIZE_VARIANT_TEXTBOX_WIDTH,
  pt: [null, null, 'sp_16', 'sp_32'],
  pr: [null, null, 'sp_16', 'sp_20'],
  pb: [null, null, 'sp_16', 'sp_32'],
  display: ['none', null, 'flex'],
  boxSizing: 'border-box',
  position: 'absolute',
  maxHeight: '90%',
};

const InnerContainer = styled(Flex)<FlexProps & BackgroundColorProps & OverflowYProps>`
  ${bg};
  ${display};
  ${overflowY};
`;

const innerContainerStyles: ComponentProps<typeof InnerContainer> = {
  width: '100%',
  overflowY: ['hidden', null, 'visible'],
};

const getContainerPositionStyles = (
  textContainerPosition: Nullable<TextContainerPosition> | undefined
) => {
  const containerPosition = textContainerPosition ?? TextContainerPosition.Left;

  switch (containerPosition) {
    case TextContainerPosition.Center:
      return {
        left: '50%',
        transform: 'translateX(-50%)',
        pl: [null, null, 'sp_16', 'sp_20'],
      };
    case TextContainerPosition.Right:
      return {
        right: 0,
        mr: [null, null, 'sp_24', 'sp_32'],
        pl: [null, null, 'sp_24', 'sp_32'],
      };
    case TextContainerPosition.Left:
    default:
      return {
        left: 0,
        ml: [null, null, 'sp_24', 'sp_32'],
        pl: [null, null, 'sp_24', 'sp_32'],
      };
  }
};

const FloatingTextBox = ({
  children,
  backgroundColor,
  textContainerOpacity,
  isTextMissing,
  textContainerPosition,
}: FloatingTextBoxProps) => {
  if (isTextMissing) return null;

  return (
    <Container
      {...containerStyles}
      {...getContainerPositionStyles(textContainerPosition)}
      bg={(backgroundColor ?? 'transparent') as Color}
      opacity={Number(textContainerOpacity) ?? 1}
      data-testid="floating-text-box"
    >
      <InnerContainer {...innerContainerStyles}>{children}</InnerContainer>
    </Container>
  );
};

export default FloatingTextBox;
